import { defineComponent } from 'vue'
import moment from 'moment'

/**
 * Route of the transaction an extraction created, carrying the log id so the data entry view
 * opens with the source document beside it. Empty for rows with no transaction yet.
 * @param row  An extraction log row
 * @return {string}
 */
const transactionPath = (row) => {
  const trnRoute = { 'AP': 'aps', 'PO': 'pos' }
  const type = row.transaction_type ? trnRoute[row.transaction_type] : ''
  if (!(type && row.transaction_id)) return ''
  return `/${type}/${row.transaction_id}?proscan_log=${row.log_id}`
}

/*
* AG Grid Column Defs here
*/
export default defineComponent({
  data () {
    const uniqueValues = (values) => [...new Set(values.filter(v => v !== null && v !== undefined && v !== ''))]

    // ag-grid puts what a renderer returns into the cell as markup, so a value read off a file the
    // user uploaded - its name - is escaped rather than trusted as part of it
    const escapeHtml = (value) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

    /**
     * Filtering is done by the API: the rows the grid holds are the ones it returned for the
     * criteria in the filters, so every one of them stays on screen. The comparisons the filters
     * make on their way there are answered here rather than narrowing the results a second time.
     *
     * @param setValues  The values a set filter offers. The row is handed all of them, so whichever
     *                   ones are ticked it counts as one of them. Anything but null keeps a row in
     *                   the text and date filters, which read a blank cell as no match.
     */
    const serverSideFilter = (setValues = null) => ({ filterValueGetter: () => setValues || '' })

    // Answers for the comparisons the text and date filters make, both of which read as a match
    const serverSideFilterParams = {
      textCustomComparator: () => true,
      comparator: () => 0
    }

    const textFilterParams = () => {
      return {
        newRowsAction: 'keep', suppressAndOrCondition: true, ...serverSideFilterParams
      }
    }

    const textFloatingFilter = {
      floatingFilter: true,
      floatingFilterComponent: 'agGridTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true
      }
    }

    /**
     * Timestamps come back in UTC, so they are shifted into the browser's timezone and the time
     * dropped. A value with no offset in it is read as UTC rather than as a local wall clock time.
     */
    const toLocalDate = (value) => {
      if (!value) return ''
      const parsed = /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? moment(value) : moment.utc(value)
      const now = moment()
      const hoursDiff = now.diff(parsed, 'hours')
      if (hoursDiff < 24) {
        return parsed.isValid() ? parsed.local().fromNow() : ''
      } else {
        return parsed.isValid() ? parsed.local().format('MM-DD-YYYY HH:mm:ss') : ''
      }
    }

    // Server side only the criteria the API understands are offered: an exact date, or a range
    const dateFilterParams = () => {
      return {
        suppressAndOrCondition: true,
        newRowsAction: 'keep',
        filterOptions: ['equals'],
        inRangeInclusive: false,
        ...serverSideFilterParams
      }
    }
    /**
     * The set filters offer every value the API knows about rather than the ones the page happens to
     * hold, so a column can still be filtered down to something none of the rows on screen are.
     * Ticking values is what the query is built from; the rows that come back are all kept.
     */
    const setFilterParams = (getValues, valueFormatter) => {
      const params = {
        suppressAndOrCondition: true,
        values: (params) => params.success(uniqueValues(getValues())),
        refreshValuesOnOpen: true,
        newRowsAction: 'keep',
        suppressSorting: true,
        buttons: ['apply', 'cancel'],
        closeOnApply: true
      }
      if (valueFormatter) params.valueFormatter = valueFormatter
      return params
    }
    const documentTypeLabels = {
      invoice: 'AP Invoice',
      purchase_order: 'Purchase Order',
      vendor_documents: 'Vendor'
    }
    const statusLabels = {
      IN_PROGRESS: 'In Progress',
      SUCCESSFUL: 'Successful',
      FAILED: 'Failed'
    }
    return {
      transactionIdColumn: {
        headerName: 'Transaction ID',
        field: 'ledger',
        colId: 'ledger',
        maxWidth: 150,
        minWidth: 120,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverSideFilter(),
        cellStyle: this.getStyles('ledger'),
        cellRenderer: (params) => {
          if (!params.data || !params.data.ledger || !params.data.transaction_id) return ''
          const path = transactionPath(params.data)
          if (!path) return `${params.data.transaction_id}`
          return `<a href="${path}" target="_blank" style="cursor: pointer; color: blue; text-decoration: none;" rel="noopener noreferrer">${params.data.ledger}</a>`
        },
        valueSetter: () => null
      },
      documentTypeColumn: {
        headerName: 'Document Type',
        field: 'document_type',
        colId: 'document_type',
        maxWidth: 120,
        minWidth: 100,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(
          () => Object.keys(documentTypeLabels),
          (params) => documentTypeLabels[params.value] || params.value
        ),
        ...serverSideFilter(Object.keys(documentTypeLabels)),
        floatingFilter: true,
        cellStyle: this.getStyles('document_type'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        cellRenderer: (params) => {
          if (!params.data || !params.data.document_type) return ''
          let type = ''
          if (params.data.document_type === 'invoice') type = 'AP Invoice'
          if (params.data.document_type === 'purchase_order') type = 'Purchase Order'
          if (params.data.document_type === 'vendor_documents') type = 'Vendor'
          return `<div class="el-tag" style="background-color: #ffffff; border-color: #d1dbe5; color: #282828;">${type}</div>`
        },
        valueSetter: () => null
      },
      fileNameColumn: {
        headerName: 'Document Name',
        field: 'file_name',
        colId: 'file_name',
        maxWidth: 250,
        minWidth: 180,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverSideFilter(),
        cellStyle: this.getStyles('file_name'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        /**
         * The file is behind a url the API hands out one request at a time, so the name cannot be
         * an href - it is rendered as a link the grid's click handler picks up, carrying the id it
         * asks for the url with. A row whose file is not there reads as plain text.
         */
        cellRenderer: (params) => {
          if (!params.data || !params.data.file_name) return ''
          const name = escapeHtml(params.data.file_name)
          const fileId = params.data.file_data && params.data.file_data.id
          if (!fileId) return name
          return `<span class="file-download-link" data-file-id="${escapeHtml(fileId)}" style="cursor: pointer; color: blue;">${name}</span>`
        },
        valueGetter: (params) => params.data ? params.data.file_name : '',
        valueSetter: () => null
      },
      vendorColumn: {
        headerName: 'Vendor',
        field: 'vendor',
        colId: 'vendor',
        maxWidth: 250,
        minWidth: 180,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Vendors are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchVendorsWithQuery',
          entityKey: 'vendors',
          placeholder: 'Search vendors',
          emptyText: 'Search for a vendor to filter by',
          serverFiltered: true
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Vendor'
        },
        cellStyle: this.getStyles('vendor'),
        /**
         * The vendor opens in the form the rest of the app edits vendors in, which the view holding
         * this grid is the one to put up: the name is rendered as a link its click handler picks
         * up, carrying the id the form is opened on.
         */
        cellRenderer: (params) => {
          const vendor = params.data && params.data.vendor_data
          if (!(vendor && vendor.name)) return ''
          const name = escapeHtml(vendor.name)
          if (!vendor.id) return name
          return `<span class="vendor-link" data-vendor-id="${escapeHtml(vendor.id)}" style="cursor: pointer; color: blue;">${name}</span>`
        },
        valueGetter: (params) => {
          if (params.data && params.data.vendor_data) {
            const vendor = params.data.vendor_data || params.data.vendor
            return vendor ? vendor.name : ''
          } else {
            return ''
          }
        }
      },
      batchColumn: {
        headerName: 'Batch',
        field: 'batch',
        colId: 'batch',
        maxWidth: 250,
        minWidth: 180,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        // Batches are searched through the API, not picked from the ones already loaded
        filter: 'agGridSearchFilter',
        filterParams: {
          searchAction: 'fetchBatchesWithQuery',
          entityKey: 'batches',
          placeholder: 'Search batches',
          emptyText: 'Search for a batch to filter by',
          serverFiltered: true
        },
        floatingFilter: true,
        floatingFilterComponent: 'agGridSearchFloatingFilter',
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          placeholder: 'Batch'
        },
        cellStyle: this.getStyles('batch'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        valueGetter: (params) => (params.data && params.data.batch_data) ? params.data.batch_data.name : '',
        valueSetter: () => null
      },
      statusColumn: {
        headerName: 'Status',
        field: 'status',
        colId: 'status',
        maxWidth: 150,
        minWidth: 100,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: setFilterParams(
          () => Object.keys(statusLabels),
          (params) => statusLabels[params.value] || params.value
        ),
        ...serverSideFilter(Object.keys(statusLabels)),
        floatingFilter: true,
        cellStyle: this.getStyles('status'),
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        cellRenderer: (params) => {
          if (!params.data || !params.data.status) return ''
          if (params.data.status === 'SUCCESSFUL') {
            return `<div class="el-tag statustag success">Successful</div>`
          }
          if (params.data.status === 'IN_PROGRESS') {
            return `<div class="el-tag statustag inprogress">In Progress</div>`
          }
          if (params.data.status === 'FAILED') {
            return `<div class="el-tag statustag failed">Failed</div>`
          }
        },
        valueSetter: () => null
      },
      infoColumn: {
        headerName: 'Info',
        field: 'info',
        colId: 'info',
        maxWidth: 400,
        minWidth: 300,
        sortable: true,
        wrapText: true,
        autoHeight: true,
        sortingOrder: ['desc', 'asc'],
        filter: 'agTextColumnFilter',
        ...textFloatingFilter,
        filterParams: textFilterParams(),
        ...serverSideFilter(),
        cellStyle: this.getStyles('info'),
        cellClassRules: {
          'ag-info-error': (params) => {
            return params.data && params.data.status === 'FAILED'
          }
        },
        comparator: (a, b) => {
          return a.localeCompare(b, undefined, { numeric: true })
        },
        cellRenderer: 'agGridIconRender',
        valueSetter: () => null
      },

      uploadedAtColumn: {
        headerName: 'Uploaded At',
        field: 'created_at',
        colId: 'created_at',
        maxWidth: 180,
        minWidth: 150,
        sortable: true,
        sort: 'desc',
        sortingOrder: ['desc', 'asc'],
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterParams: dateFilterParams(),
        ...serverSideFilter(),
        cellStyle: this.getStyles('created_at'),
        comparator: (date1, date2) => {
          const dateObj1 = new Date(date1)
          const dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        },
        tooltipValueGetter: (params) => {
          if (!params.data.created_at) return ''
          const parsed = /(Z|[+-]\d{2}:?\d{2})$/.test(params.data.created_at) ? moment(params.data.created_at) : moment.utc(params.data.created_at)
          return parsed.isValid() ? parsed.local().format('MM-DD-YYYY HH:mm:ss') : ''
        },
        valueGetter: (params) => params.data ? toLocalDate(params.data.created_at) : '',
        valueSetter: () => null
      }
    }
  },
  methods: {
    getStyles (column) {
      if (column === 'amount') {
        return {
          'border-right-color': '#e2e2e2',
          'display': 'flex ',
          'align-items': 'center',
          'justify-content': 'end'
        }
      }
      if (column) {
        return {
          'display': 'flex ',
          'align-items': 'center ',
          'border-right-color': '#e2e2e2',
          'border-top': 'none',
          'border-bottom': 'none',
          'border-left': 'none'
        }
      }
      return {
        'display': 'flex ',
        'align-items': 'center',
        'color': 'blue',
        'border-right-color': '#e2e2e2',
        'border-top': 'none',
        'border-bottom': 'none',
        'border-left': 'none'
      }
    }
  }
})
